import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ScreenLock({ children }) {
  const [isLocked, setIsLocked] = useState(false);
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState({
    bloqueio_inatividade_ativo: false,
    tempo_inatividade_minutos: 5,
    tem_senha_desbloqueio: false
  });

  // Carregar configurações de segurança
  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/settings/security`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config de segurança:', error);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Monitorar inatividade
  useEffect(() => {
    if (!config.bloqueio_inatividade_ativo || !config.tem_senha_desbloqueio) {
      return;
    }

    let timeoutId;
    const tempoMs = config.tempo_inatividade_minutos * 60 * 1000;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      if (!isLocked) {
        timeoutId = setTimeout(() => {
          setIsLocked(true);
          setSenha('');
          setError('');
        }, tempoMs);
      }
    };

    // Eventos que indicam atividade
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Iniciar timer
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
    };
  }, [config.bloqueio_inatividade_ativo, config.tempo_inatividade_minutos, config.tem_senha_desbloqueio, isLocked]);

  // Desbloquear tela
  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!senha.trim()) {
      setError('Digite a senha');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/security/verify-password`, {
        tipo: 'desbloqueio',
        senha: senha
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsLocked(false);
      setSenha('');
      toast.success('Tela desbloqueada!');
    } catch (error) {
      setError(error.response?.data?.detail || 'Senha incorreta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {children}
      
      {/* Tela de bloqueio totalmente opaca */}
      {isLocked && (
        <div 
          className="fixed inset-0 z-[9999] bg-background flex items-center justify-center"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999
          }}
          data-testid="screen-lock-overlay"
        >
          <div className="w-full max-w-md mx-4 p-6 bg-card border rounded-lg shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">Tela Bloqueada</h2>
              <p className="text-muted-foreground mt-2">
                O sistema foi bloqueado por inatividade. Digite a senha para continuar.
              </p>
            </div>

            <form onSubmit={handleUnlock} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="senha-desbloqueio">Senha</Label>
                <div className="relative">
                  <Input
                    id="senha-desbloqueio"
                    type={showSenha ? 'text' : 'password'}
                    placeholder="Digite a senha"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    autoFocus
                    className="pr-10"
                    data-testid="input-unlock-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowSenha(!showSenha)}
                  >
                    {showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-2 rounded">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading} data-testid="btn-unlock-screen">
                {loading ? 'Verificando...' : 'Desbloquear'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// Hook para verificar acesso ao financeiro com expiração de 30 minutos
const FINANCEIRO_SESSION_KEY = 'financeiro_access_timestamp';
const FINANCEIRO_EXPIRY_MINUTES = 30;

export function useFinanceiroAccess() {
  const [isVerified, setIsVerified] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [config, setConfig] = useState({ bloqueio_financeiro_ativo: false, tem_senha_financeiro: false });

  // Verificar se a sessão ainda é válida
  const checkSessionValidity = useCallback(() => {
    const timestamp = sessionStorage.getItem(FINANCEIRO_SESSION_KEY);
    if (!timestamp) return false;
    
    const accessTime = parseInt(timestamp, 10);
    const now = Date.now();
    const expiryMs = FINANCEIRO_EXPIRY_MINUTES * 60 * 1000;
    
    if (now - accessTime > expiryMs) {
      // Sessão expirou
      sessionStorage.removeItem(FINANCEIRO_SESSION_KEY);
      return false;
    }
    return true;
  }, []);

  const checkConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/settings/security`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      
      // Se não tiver bloqueio ativo, já está verificado
      if (!response.data.bloqueio_financeiro_ativo || !response.data.tem_senha_financeiro) {
        setIsVerified(true);
      } else {
        // Verificar se a sessão ainda é válida
        const sessionValid = checkSessionValidity();
        setIsVerified(sessionValid);
      }
    } catch (error) {
      console.error('Erro ao verificar config:', error);
      setIsVerified(true); // Em caso de erro, permitir acesso
    }
  }, [checkSessionValidity]);

  useEffect(() => {
    checkConfig();
    
    // Verificar a cada minuto se a sessão expirou
    const interval = setInterval(() => {
      if (config.bloqueio_financeiro_ativo && config.tem_senha_financeiro) {
        const sessionValid = checkSessionValidity();
        if (!sessionValid && isVerified) {
          setIsVerified(false);
        }
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [checkConfig, checkSessionValidity, config, isVerified]);

  const requestAccess = useCallback(() => {
    // Verificar novamente a validade da sessão
    if (config.bloqueio_financeiro_ativo && config.tem_senha_financeiro) {
      const sessionValid = checkSessionValidity();
      if (sessionValid) {
        setIsVerified(true);
        return true;
      }
      setShowModal(true);
      return false;
    }
    return true;
  }, [config, checkSessionValidity]);

  const verifyPassword = async (senha) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/security/verify-password`, {
        tipo: 'financeiro',
        senha: senha
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Salvar timestamp da verificação
      sessionStorage.setItem(FINANCEIRO_SESSION_KEY, Date.now().toString());
      setIsVerified(true);
      setShowModal(false);
      return true;
    } catch (error) {
      throw error;
    }
  };

  return {
    isVerified,
    showModal,
    setShowModal,
    requestAccess,
    verifyPassword,
    needsVerification: config.bloqueio_financeiro_ativo && config.tem_senha_financeiro
  };
}

// Componente de modal para acesso ao financeiro
export function FinanceiroAccessModal({ open, onOpenChange, onVerify }) {
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!senha.trim()) {
      setError('Digite a senha');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onVerify(senha);
      setSenha('');
      toast.success('Acesso liberado!');
    } catch (error) {
      setError(error.response?.data?.detail || 'Senha incorreta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-amber-500" />
          </div>
          <DialogTitle className="text-xl">Acesso ao Financeiro</DialogTitle>
          <DialogDescription>
            Digite a senha para acessar o módulo financeiro.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="senha-financeiro">Senha</Label>
            <div className="relative">
              <Input
                id="senha-financeiro"
                type={showSenha ? 'text' : 'password'}
                placeholder="Digite a senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoFocus
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowSenha(!showSenha)}
              >
                {showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-2 rounded">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Verificando...' : 'Acessar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
